import { type KeyboardEventHandler, useEffect, useState } from 'react'
import { useSnackbar } from 'notistack'
import { type SubmitHandler, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import { Checkbox } from '@mui/material'

import CircleApplyCountdown from '~/features/circle-participation/components/CircleApplyCountdown'
import GenreTag from '~/features/circle-participation/components/GenreTag'
import {
  useApplyUserCircleBoothform,
  useCircleBoothform,
} from '~/features/circle-participation/queries/useCircleApply'
import { checkOverLargeBoothType } from '~/features/circle-participation/utils/function'
import {
  type BoothType,
  type ComplexityType,
  type DateType,
  type ExpType,
  type Goods,
  type Image,
  type ItemType,
  type SizeType,
  type ZoneType,
} from '~/features/circle-participation/utils/type'
import { useFileUpload } from '~/features/file-upload/queries/useFileUpload'
import { useOngoingEvent } from '~/features/main/queries/useMainContents'
import Check from '~/shared/assets/icons/Check'
import ImageUpload from '~/shared/assets/icons/ImageUpload'
import Sii2 from '~/shared/assets/images/sii2.png'
import Button from '~/shared/components/buttons/Button'
import LoadingScreen from '~/shared/components/loading/LoadingScreen'
import { checkEmailValidation } from '~/shared/components/modals/auth/checkEmailValidation'
import Tag from '~/shared/components/tags/Tag'
import useViewport from '~/shared/hooks/useViewport'
import theme from '~/shared/styles/theme'
import { checkPhoneNumberValidation } from '~/shared/utils/checkPhoneNumberValidation'

interface IForm {
  boothName: string
  dateType: keyof typeof DateType
  boothType: keyof typeof BoothType
  boothOptionType: string
  zoneType: keyof typeof ZoneType
  userName: string
  password: string
  userEmail: string
  userMobile: string
  nearBooth: string
  representativeImage: string
  sizeType: keyof typeof SizeType
  complexityType: keyof typeof ComplexityType
  expType: keyof typeof ExpType
  goodsTypeList: ItemType[] | []
  goodsList: Goods[]
  genreTypeList: ItemType[] | []
  genreTags: string[]
  representationNickname: string
  introduce: string
  homepage: string
  goodsInfoImage: string[]
  imageInfo: Image
}

function CircleApplicationForm() {
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const viewport = useViewport()
  const isMobile = viewport <= theme.deviceSizes.medium

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isSuperBooth, setIsSuperBooth] = useState<boolean>(false)
  const [isNoticeAgreed, setIsNoticeAgreed] = useState<boolean>(false)

  const [uploadedCircleImage, setUploadedCircleImage] = useState<Image>()
  const [uploadedGoodsImageList, setUploadedGoodsImageList] = useState<Image[]>(
    [],
  )

  const { data: ongoingEvent } = useOngoingEvent()
  const { data: circleBoothform, refetch } = useCircleBoothform(
    ongoingEvent?.data.eventInfo.id.toString() || '',
  )

  const { mutate: fileUploadMutate } = useFileUpload()

  const {
    start_at = '',
    date_type_list = [],
    booth_type_list = [],
    zone_type_list = [],
  } = circleBoothform?.data.boothApplication || {}

  const { mutate: userCircleBoothformMutate } = useApplyUserCircleBoothform()

  const { register, watch, handleSubmit, setValue } = useForm<IForm>({
    mode: 'onSubmit',
    defaultValues: {
      boothName: '',
      dateType: undefined,
      boothType: undefined,
      boothOptionType: '',
      zoneType: undefined,
      userName: '',
      userEmail: '',
      userMobile: '',
      nearBooth: '',
      representativeImage: '',
      sizeType: undefined,
      complexityType: undefined,
      expType: undefined,
      goodsTypeList: [],
      goodsList: [],
      genreTypeList: [],
      genreTags: [],
      representationNickname: '',
      introduce: '',
      homepage: '',
      goodsInfoImage: [],
      imageInfo: undefined,
    },
  })

  const [
    dateType,
    boothType,
    zoneType,
    userMobile,
    boothOptionType,
    userEmail,
    sizeType,
    goodsTypeList,
    genreTags,
    complexityType,
    expType,
    genreTypeList,
    imageInfo,
    goodsList,
  ] = watch([
    'dateType',
    'boothType',
    'zoneType',
    'userMobile',
    'boothOptionType',
    'userEmail',
    'sizeType',
    'goodsTypeList',
    'genreTags',
    'complexityType',
    'expType',
    'genreTypeList',
    'imageInfo',
    'goodsList',
  ])

  const uploadCircleImageToServer = async (file: File) => {
    if (!checkFileSize(file, 2)) return
    if (file) {
      await fileUploadMutate(file, {
        onSuccess: (res) => {
          if (res.data.errorCode === 0) {
            const ImageData = {
              id: res.data.data.file_list[0].id,
              url: res.data.data.file_list[0].url,
            }

            setUploadedCircleImage(ImageData)
            enqueueSnackbar('파일 업로드가 완료되었습니다.', {
              variant: 'success',
            })
          } else if (res.data.errorCode < 0) {
            enqueueSnackbar(res.data.errorMsg, { variant: 'error' })
          }
        },
        onError: (error) => {
          enqueueSnackbar(error.message, { variant: 'error' })
        },
      })
    }
  }

  const checkFileSize = (file: File, maxSizeMB: number): boolean => {
    const MAX_FILE_SIZE = maxSizeMB * 1024 * 1024
    if (file.size > MAX_FILE_SIZE) {
      enqueueSnackbar(`파일 크기는 ${maxSizeMB}MB를 초과할 수 없습니다.`, {
        variant: 'error',
      })

      return false
    }

    return true
  }

  const uploadGoodsImageToServer = async (file: File) => {
    // 파일 사이즈 체크
    if (!checkFileSize(file, 5)) return

    await fileUploadMutate(file, {
      onSuccess: (res) => {
        if (res.data.errorCode === 0) {
          const ImageData = {
            id: res.data.data.file_list[0].id,
            url: res.data.data.file_list[0].url,
          }

          setUploadedGoodsImageList([...uploadedGoodsImageList, ImageData])
          enqueueSnackbar('파일 업로드가 완료되었습니다.', {
            variant: 'success',
          })
        } else if (res.data.errorCode < 0) {
          enqueueSnackbar(res.data.errorMsg, { variant: 'error' })
        }
      },
      onError: (error) => {
        enqueueSnackbar(error.message, { variant: 'error' })
      },
    })
  }

  const SelectObjectMultiple = (
    selected: ItemType,
    typeList: ItemType[],
    key: string,
  ) => {
    if (
      typeList.findIndex(
        (object: ItemType) => object.code === selected.code,
      ) === -1
    ) {
      setValue(key as keyof IForm, [...typeList, selected])
    } else {
      setValue(
        key as keyof IForm,
        typeList.filter((object: ItemType) => object.code !== selected.code),
      )
    }
  }

  const checkKeyDown: KeyboardEventHandler<
    HTMLFormElement | HTMLTextAreaElement
  > = (e) => {
    const target = e.target as HTMLElement
    // textarea 안에서는 Enter 키 입력을 허용
    if (e.key === 'Enter' && target.tagName.toLowerCase() !== 'textarea') {
      e.preventDefault()
    }
  }

  const onSubmit: SubmitHandler<IForm> = (data) => {
    setIsLoading(true)

    if (
      circleBoothform?.data.boothApplication.id &&
      circleBoothform?.data.boothApplication.event_id &&
      data.boothName &&
      data.dateType &&
      data.boothType &&
      data.zoneType &&
      data.userName &&
      data.userEmail &&
      data.userMobile
    ) {
      const userBoothApplication = {
        event_booth_application_id: circleBoothform?.data.boothApplication.id,
        event_id: circleBoothform?.data.boothApplication.event_id,
        booth_name: data.boothName,
        date_type: data.dateType,
        booth_type: data.boothType,
        booth_option_type: data.boothOptionType,
        zone_type: data.zoneType,
        user_name: data.userName,
        user_email: data.userEmail,
        user_mobile: data.userMobile,
        near_booth: data.nearBooth,
        size_type: sizeType,
        complexity_type: complexityType,
        exp_type: expType,
        goods_type: goodsTypeList.map((goods) => goods.code).join(),
        genre_type: genreTypeList.map((genre) => genre.code).join(),
        tag: genreTags.join(),
        user_nickname: data.representationNickname,
        introduce: data.introduce,
        homepage: data.homepage,
        image: uploadedCircleImage?.id || imageInfo?.id || 0,
        goods_list:
          uploadedGoodsImageList.length > 0
            ? uploadedGoodsImageList
            : goodsList,
      }

      if (!checkEmailValidation(data.userEmail)) {
        enqueueSnackbar('이메일 형식이 유효하지 않습니다.', {
          variant: 'error',
        })

        return setIsLoading(false)
      }

      if (!checkPhoneNumberValidation(data.userMobile)) {
        enqueueSnackbar('전화번호 형식이 유효하지 않습니다.', {
          variant: 'error',
        })

        return setIsLoading(false)
      }

      if (isSuperBooth && !data.boothOptionType) {
        enqueueSnackbar('희망하는 초대형 부스 종류를 선택해 주세요.', {
          variant: 'error',
        })

        return setIsLoading(false)
      } else if (data.boothName.length > 21) {
        enqueueSnackbar('서클 부스 이름은 최대 20자까지 가능합니다.', {
          variant: 'error',
        })

        return setIsLoading(false)
      } else if (!isNoticeAgreed) {
        enqueueSnackbar('공지사항을 읽고 동의해 주세요.', { variant: 'error' })

        return setIsLoading(false)
      } else if (isNoticeAgreed) {
        userCircleBoothformMutate(
          { userBoothApplication },
          {
            onSuccess: (res) => {
              if (res.errorCode === 0) {
                enqueueSnackbar('신청서 등록이 완료되었습니다.', {
                  variant: 'success',
                })

                navigate('/circle-participation/apply-list')

                return setIsLoading(false)
              } else {
                enqueueSnackbar(res.errorMsg, { variant: 'error' })

                return setIsLoading(false)
              }
            },
          },
        )
      }
    } else {
      enqueueSnackbar('필수 정보를 모두 입력하세요.', { variant: 'error' })

      return setIsLoading(false)
    }
  }

  useEffect(() => {
    refetch()

    if (circleBoothform) {
      if (circleBoothform.data.user_event_application_count > 0) {
        enqueueSnackbar('이미 등록된 서클 신청서가 있습니다.', {
          variant: 'error',
        })
      }
    }
  }, [ongoingEvent])

  if (isLoading) return <LoadingScreen />

  return (
    <Wrapper>
      <>
        {circleBoothform?.data && circleBoothform?.data.boothApplication ? (
          <>
            <TitleBox>
              <Title>서클 부스 참가 신청</Title>
              <InquiryButton
                $width={175}
                $height={58}
                $fontSize={24}
                $variant="transparent"
                onClick={() => navigate('/customer-service/inquiry')}
              >
                1:1 문의하기
              </InquiryButton>
            </TitleBox>
            <TagBox>
              <Tag $size="sm" $variant="gray">
                접수 기간
              </Tag>
              <div className="desc">
                {circleBoothform?.data.boothApplication.period}
              </div>
            </TagBox>
            <FestivalInfoBox>
              <SubTitle>{circleBoothform?.data.event_info.name}</SubTitle>
              <TagBox>
                <Tag $size="sm" $variant="gray">
                  개최일
                </Tag>
                <div className="desc">
                  {circleBoothform?.data.event_info.show_date}
                </div>
              </TagBox>
              <TagBox>
                <Tag $size="sm" $variant="gray">
                  개최 장소
                </Tag>
                <div className="desc">
                  {circleBoothform?.data.event_info.place}
                </div>
              </TagBox>
              <TagBox className="start">
                <Tag $size="sm" $variant="gray">
                  주의사항
                </Tag>
                <div
                  className="desc"
                  dangerouslySetInnerHTML={{
                    __html:
                      circleBoothform?.data.boothApplication.caution || '-',
                  }}
                ></div>
              </TagBox>
              <TagBox className="start">
                <Tag $size="sm" $variant="gray">
                  취급 불가 항목
                </Tag>
                <div
                  className="desc"
                  dangerouslySetInnerHTML={{
                    __html:
                      circleBoothform?.data.boothApplication.prohibit || '-',
                  }}
                ></div>
              </TagBox>
            </FestivalInfoBox>
            <NoticeAgreementBox>
              <Notice>
                「서클 규정」 및 「취급 불가 항목」을 숙지하였으며, AI 합성
                이미지를 취급하지 않음을 동의합니다.
              </Notice>
              <CheckBoxWrapper>
                <StyledCheckbox
                  onChange={(e) => setIsNoticeAgreed(e.target.checked)}
                />
                <CheckBoxText>동의</CheckBoxText>
              </CheckBoxWrapper>
            </NoticeAgreementBox>
            <CircleInfoBox
              onSubmit={handleSubmit(onSubmit)}
              onKeyDown={(e) => checkKeyDown(e)}
            >
              <RequiredBox>
                <SubTitle>
                  서클 정보{' '}
                  <div className="warning">
                    *결제 요청을 받으시면 그 이후부터는 정보 수정이 불가합니다.
                  </div>
                </SubTitle>

                <InputBox className="center">
                  <div className="title">서클 이름</div>
                  <input className="text-input" {...register('boothName')} />
                </InputBox>
                <InputBox>
                  <div className="title">참가 희망일</div>
                  <SelectButtonBox className="column">
                    {date_type_list.map((date: any) => (
                      <SelectButton
                        key={date.code}
                        className={dateType === date.code ? 'active' : ''}
                        onClick={() => {
                          if (
                            boothType === 'B0200004' &&
                            date.code !== 'E0300003'
                          ) {
                            setIsSuperBooth(false)
                            enqueueSnackbar(
                              '대형 부스 이상은 양일 신청 시에만 가능합니다.',
                              { variant: 'error' },
                            )
                          } else {
                            setValue('dateType', date.code)
                          }
                        }}
                      >
                        {dateType === date.code ? (
                          <Check
                            color={theme.colors.white}
                            width={21}
                            height={21}
                          />
                        ) : null}
                        {date.description}
                      </SelectButton>
                    ))}
                  </SelectButtonBox>
                </InputBox>
                <div className="warning">
                  *대형 부스 이상은 양일 신청 시에만 진행 가능합니다.
                </div>
                <div className="horizontal" />
                <InputBox>
                  <div className="title">부스 신청 사양</div>
                  <SelectButtonBox className="column">
                    {booth_type_list.map((booth: any) => {
                      if (booth.is_option === 'N') {
                        return (
                          <SelectButton
                            key={booth.code}
                            className={boothType === booth.code ? 'active' : ''}
                            onClick={() => {
                              if (
                                booth.code === 'B0200004' &&
                                dateType !== 'E0300003'
                              ) {
                                enqueueSnackbar(
                                  '대형 부스 이상은 양일 신청 시에만 가능합니다.',
                                  { variant: 'error' },
                                )
                              } else {
                                setIsSuperBooth(false)
                                setValue('boothOptionType', '')
                                setValue('boothType', booth.code)
                              }
                            }}
                          >
                            {boothType === booth.code ? (
                              <Check
                                color={theme.colors.white}
                                width={21}
                                height={21}
                              />
                            ) : null}
                            {booth.description} : {booth.booth_description}
                          </SelectButton>
                        )
                      }
                    })}

                    <div className="divider" />
                    {isMobile && checkOverLargeBoothType(boothType) && (
                      <ButtonBox>
                        <div
                          className={`button ${isSuperBooth ? 'active' : ''}`}
                          onClick={() => setIsSuperBooth(true)}
                        >
                          {isSuperBooth && (
                            <Check
                              color={theme.colors.white}
                              width={21}
                              height={21}
                            />
                          )}
                          초대형부스 희망
                        </div>
                        <div
                          className={`button ${!isSuperBooth ? 'active' : ''}`}
                          onClick={() => {
                            setValue('boothType', 'B0200004')
                            setIsSuperBooth(false)
                          }}
                        >
                          {!isSuperBooth ? (
                            <Check
                              color={theme.colors.white}
                              width={21}
                              height={21}
                            />
                          ) : null}
                          비희망
                        </div>
                      </ButtonBox>
                    )}
                    {isSuperBooth &&
                      booth_type_list.map((booth: any) => {
                        if (booth.is_option === 'Y') {
                          return (
                            <SelectButton
                              key={booth.code}
                              className={
                                boothOptionType === booth.code ? 'active' : ''
                              }
                              onClick={() => {
                                setValue('boothOptionType', booth.code)
                                setValue('boothType', 'B0200004')
                              }}
                            >
                              {boothOptionType === booth.code ? (
                                <Check
                                  color={theme.colors.white}
                                  width={21}
                                  height={21}
                                />
                              ) : null}
                              {booth.description} :{booth.booth_description}
                            </SelectButton>
                          )
                        }
                      })}
                  </SelectButtonBox>

                  {!isMobile && checkOverLargeBoothType(boothType) && (
                    <BubbleBox>
                      <div>초대형부스 업그레이드 희망</div>
                      <ButtonBox>
                        <div
                          className={`button ${isSuperBooth ? 'active' : ''}`}
                          onClick={() => setIsSuperBooth(true)}
                        >
                          {isSuperBooth && (
                            <Check
                              color={theme.colors.white}
                              width={21}
                              height={21}
                            />
                          )}
                          희망
                        </div>
                        <div
                          className={`button ${!isSuperBooth ? 'active' : ''}`}
                          onClick={() => {
                            setValue('boothType', 'B0200004')
                            setIsSuperBooth(false)
                          }}
                        >
                          {!isSuperBooth ? (
                            <Check
                              color={theme.colors.white}
                              width={21}
                              height={21}
                            />
                          ) : null}
                          비희망
                        </div>
                      </ButtonBox>
                      <Poligon />
                    </BubbleBox>
                  )}
                </InputBox>

                <div className="warning">
                  *초대형 부스 신청을 요청 하더라도 행사장 사정에따라 신청이
                  거절될 수 있으며, 거절된 경우 일반 대형 부스로 신청이
                  진행됩니다.
                </div>
                <div className="horizontal" />
                <InputBox>
                  <div className="title">특별존 참가</div>
                  <SelectButtonBox className="column">
                    {zone_type_list.map((zone: any) => (
                      <SelectButton
                        key={zone.code}
                        className={zoneType === zone.code ? 'active' : ''}
                        onClick={() => setValue('zoneType', zone.code)}
                      >
                        {zoneType === zone.code ? (
                          <Check
                            color={theme.colors.white}
                            width={21}
                            height={21}
                          />
                        ) : null}
                        {zone.description}
                      </SelectButton>
                    ))}
                  </SelectButtonBox>
                </InputBox>
                <div className="horizontal" />
                <InputBox className="center">
                  <div className="title">대표자명</div>
                  <input className="text-input" {...register('userName')} />
                </InputBox>
                <div className="horizontal" />
                <InputBox className="center">
                  <div className="title">이메일</div>
                  <input
                    className="text-input"
                    onChange={(e) => {
                      if (e.target.value) {
                        setValue('userEmail', e.target.value)
                      }
                    }}
                  />
                  {!checkEmailValidation(userEmail) && (
                    <WarningText>*이메일 형식이 유효하지 않습니다.</WarningText>
                  )}
                </InputBox>
                <div className="horizontal" />
                <InputBox className="center">
                  <div className="title">대표자 연락처</div>
                  <input
                    className="text-input"
                    onChange={(e) => {
                      if (e.target.value) {
                        setValue('userMobile', e.target.value)
                      }
                    }}
                  />
                  {!checkPhoneNumberValidation(userMobile) && (
                    <WarningText>
                      *휴대폰 번호 형식이 유효하지 않습니다.
                    </WarningText>
                  )}
                </InputBox>
              </RequiredBox>
              <CircleApplyCountdown start_at={start_at} />
              <ApplyButton
                type="submit"
                value="신청서 등록"
                disabled={isLoading}
              />
              <OptionalBox>
                <SubTitle>
                  서클 정보 추가 입력
                  <div className="warning">
                    * 아래부터는 선택 입력 칸입니다. 추후에도 입력 가능합니다.
                  </div>
                </SubTitle>

                <InputBox className="center">
                  <div className="title">인접 희망 서클 이름 (선택 입력)</div>
                  <input className="text-input" {...register('nearBooth')} />
                  <span className="desc"></span>
                </InputBox>
                <div className="horizontal" />
                <InputBox>
                  <div className="title">서클 대표 이미지 등록</div>
                  <FileLabel htmlFor="file">
                    <ImageUpload />
                    이미지 등록
                    <FileInput
                      type="file"
                      name="image"
                      accept="image/*"
                      onChange={(e) => {
                        if (e.target.files) {
                          const file = e.target.files[0]
                          uploadCircleImageToServer(file)
                        }
                      }}
                    />
                  </FileLabel>

                  {uploadedCircleImage?.url && (
                    <img
                      className="thumbnail"
                      src={uploadedCircleImage.url}
                      alt="프로필 이미지"
                    />
                  )}
                </InputBox>
                <div className="warning">
                  *300x300px 권장, jpg/png의 2MB 이하 이미지
                </div>
                <div className="horizontal" />
                <InputBox>
                  <div className="title">서클 규모 </div>
                  <SelectButtonBox>
                    {circleBoothform &&
                      circleBoothform?.data.boothApplication &&
                      circleBoothform?.data.boothApplication.size_type_list?.map(
                        (size) => (
                          <SelectButton
                            key={size.code}
                            className={size.code === sizeType ? 'active' : ''}
                            onClick={() =>
                              setValue(
                                'sizeType',
                                size.code as keyof typeof SizeType,
                              )
                            }
                          >
                            {size.code === sizeType ? (
                              <Check
                                color={theme.colors.white}
                                width={21}
                                height={21}
                              />
                            ) : null}
                            {size.description}
                          </SelectButton>
                        ),
                      )}
                  </SelectButtonBox>
                </InputBox>
                <div className="warning">
                  *부스에 상주하는 인원의 규모가 아닌, 서클에 출품된 작품에
                  함께한 인원수를 반영해주세요.
                </div>
                <div className="horizontal" />
                <InputBox>
                  <div className="title">서클 인파 예상 </div>
                  <SelectButtonBox>
                    {circleBoothform?.data.boothApplication &&
                      circleBoothform?.data.boothApplication.complexity_type_list?.map(
                        (complexity) => (
                          <SelectButton
                            key={complexity.code}
                            className={
                              complexity.code === complexityType ? 'active' : ''
                            }
                            onClick={() =>
                              setValue(
                                'complexityType',
                                complexity.code as keyof typeof ComplexityType,
                              )
                            }
                          >
                            {complexity.code === complexityType ? (
                              <Check
                                color={theme.colors.white}
                                width={21}
                                height={21}
                              />
                            ) : null}
                            {complexity.description}
                          </SelectButton>
                        ),
                      )}
                  </SelectButtonBox>
                </InputBox>
                <div className="warning">
                  *최근 참여한 행사 5회 이내에, 부스 앞 대기열이 발생한 적이
                  있다면 체크해주세요.
                </div>
                <div className="horizontal" />
                <InputBox>
                  <div className="title">동인행사 서클참가 경험</div>
                  <SelectButtonBox>
                    {circleBoothform?.data.boothApplication &&
                      circleBoothform?.data.boothApplication.exp_type_list?.map(
                        (exp) => (
                          <SelectButton
                            key={exp.code}
                            className={exp.code === expType ? 'active' : ''}
                            onClick={() =>
                              setValue(
                                'expType',
                                exp.code as keyof typeof ExpType,
                              )
                            }
                          >
                            {exp.code === expType ? (
                              <Check
                                color={theme.colors.white}
                                width={21}
                                height={21}
                              />
                            ) : null}
                            {exp.description}
                          </SelectButton>
                        ),
                      )}
                  </SelectButtonBox>
                </InputBox>
                <div className="horizontal" />
                <InputBox>
                  <div className="title">취급 굿즈 (복수 선택 가능)</div>
                  <SelectButtonBox>
                    {circleBoothform?.data.boothApplication &&
                      circleBoothform?.data.boothApplication.goods_type_list.map(
                        (goods: ItemType) => {
                          return (
                            <SelectButton
                              key={goods.code}
                              className={
                                goodsTypeList &&
                                goodsTypeList.findIndex((selectedGoods) => {
                                  return goods.code === selectedGoods.code
                                }) !== -1
                                  ? 'active'
                                  : ''
                              }
                              onClick={() => {
                                SelectObjectMultiple(
                                  goods,
                                  goodsTypeList,
                                  'goodsTypeList',
                                )
                              }}
                            >
                              {goodsTypeList &&
                              goodsTypeList.findIndex(
                                (selectedGoods) =>
                                  goods.code === selectedGoods.code,
                              ) !== -1 ? (
                                <Check
                                  color={theme.colors.white}
                                  width={21}
                                  height={21}
                                />
                              ) : null}
                              {goods.description}
                            </SelectButton>
                          )
                        },
                      )}
                  </SelectButtonBox>
                </InputBox>
                <div className="horizontal" />
                <InputBox>
                  <div className="title">장르 대분류 (복수 선택 가능)</div>
                  <SelectButtonBox>
                    {circleBoothform?.data.boothApplication &&
                      circleBoothform?.data.boothApplication.genre_type_list?.map(
                        (genre: ItemType) => {
                          return (
                            <SelectButton
                              key={genre.code}
                              className={
                                genreTypeList &&
                                genreTypeList.findIndex(
                                  (selectedGenre) =>
                                    genre.code === selectedGenre.code,
                                ) !== -1
                                  ? 'active'
                                  : ''
                              }
                              onClick={() =>
                                SelectObjectMultiple(
                                  genre,
                                  genreTypeList,
                                  'genreTypeList',
                                )
                              }
                            >
                              {genreTypeList &&
                              genreTypeList.findIndex(
                                (selectedGenre) =>
                                  genre.code === selectedGenre.code,
                              ) !== -1 ? (
                                <Check
                                  color={theme.colors.white}
                                  width={21}
                                  height={21}
                                />
                              ) : null}
                              {genre.description}
                            </SelectButton>
                          )
                        },
                      )}
                  </SelectButtonBox>
                </InputBox>
                <div className="horizontal" />
                <InputBox className="center">
                  <div className="title">세부 장르 태그 </div>
                  <GenreTag
                    genreTags={genreTags}
                    setGenreTags={(tags) => setValue('genreTags', tags)}
                  />
                </InputBox>
                <div className="warning">*태그 입력 후 Enter를 눌러주세요.</div>
                <div className="horizontal" />
                <InputBox className="center">
                  <div className="title">대표자 닉네임 </div>
                  <input
                    className="text-input"
                    type="text"
                    {...register('representationNickname')}
                  />
                </InputBox>
                <div className="horizontal" />
                <InputBox>
                  <div className="title">서클 소개 </div>
                  <TextArea
                    {...register('introduce')}
                    onKeyDown={(e) =>
                      checkKeyDown(
                        e as React.KeyboardEvent<HTMLTextAreaElement>,
                      )
                    }
                  />
                </InputBox>
                <InputBox className="center">
                  <div className="title">홍보 페이지 url </div>
                  <input
                    className="text-input"
                    type="text"
                    {...register('homepage')}
                  />
                </InputBox>
                <div className="horizontal" />
                <InputBox>
                  <div className="title">부스 인포</div>

                  <FileLabel htmlFor="file">
                    <ImageUpload />
                    이미지 등록
                    <FileInput
                      type="file"
                      name="image"
                      accept="image/*"
                      onChange={(e) => {
                        if (e.target.files) {
                          const file = e.target.files[0]
                          uploadGoodsImageToServer(file)
                        }
                      }}
                    />
                  </FileLabel>
                  {uploadedGoodsImageList.length > 0 ? (
                    <>
                      {uploadedGoodsImageList?.map((goods: any) => {
                        return (
                          <img
                            key={goods.id}
                            className="thumbnail"
                            src={goods.url}
                            alt="부스 인포"
                          />
                        )
                      })}
                    </>
                  ) : (
                    <>
                      {goodsList?.map((goods: any) => {
                        return (
                          <img
                            key={goods.id}
                            className="thumbnail"
                            src={goods.url}
                            alt="부스 인포"
                          />
                        )
                      })}
                    </>
                  )}
                </InputBox>
                <div className="warning">*jpg/png의 5MB 이하 이미지</div>
              </OptionalBox>
              <CircleApplyCountdown start_at={start_at} />
              <ApplyButton
                type="submit"
                value="신청서 등록"
                disabled={isLoading}
              />
            </CircleInfoBox>
          </>
        ) : (
          <>
            <SiiImage src={Sii2} alt="sii2" />
            <Desc>서클 신청 기간이 아닙니다.</Desc>
          </>
        )}
      </>
    </Wrapper>
  )
}

export default CircleApplicationForm

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 60px;
  margin: 50px 0;

  @media ${theme.device.medium} {
    gap: 24px;
    margin: 0;
  }
`

const TitleBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media ${theme.device.medium} {
    flex-direction: column;
    gap: 20px;
    align-items: start;
  }
`

const InquiryButton = styled(Button)`
  color: ${theme.colors.black};
  background-color: ${theme.colors.white};
  border: 1px solid ${theme.colors.grey_5};

  @media ${theme.device.medium} {
    width: 100%;
  }
`

const Title = styled.h2`
  font-family: NanumSquareNeo-Bold, sans-serif;
  font-size: 42px;

  @media ${theme.device.medium} {
    font-size: 28px;
  }
`

const TagBox = styled.div`
  display: flex;
  gap: 36px;
  align-items: center;

  &.start {
    align-items: start;
  }

  .desc {
    font-family: NanumSquareNeo-Regular, sans-serif;
    line-height: 140%;
    white-space: pre-wrap;
  }

  @media ${theme.device.medium} {
    flex-direction: column;
    gap: 16px;
    align-items: start;
  }
`

const FestivalInfoBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  height: auto;
  padding: 36px 24px;

  background-color: ${theme.colors.white};
  border: 1px solid ${theme.colors.grey_3};
  border-radius: 12px;
`

const SubTitle = styled.h3`
  display: flex;
  gap: 30px;
  align-items: center;

  margin-bottom: 16px;

  font-family: NanumSquareNeo-Bold, sans-serif;
  font-size: 32px;
  line-height: 44.8px;

  @media ${theme.device.medium} {
    flex-direction: column;
    font-size: 21px;
    line-height: 29px;
  }
`

const CircleInfoBox = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
  height: auto;

  .warning {
    font-family: NanumSquareNeo-Regular, sans-serif;
    font-size: 15px;
    line-height: 21px;
    color: ${theme.colors.warning};
  }

  .horizontal {
    width: 100%;
    border-bottom: 1px solid ${theme.colors.grey_2};
  }
`

const RequiredBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  padding: 36px 24px;

  background-color: ${theme.colors.white};
  border: 1px solid ${theme.colors.grey_3};
  border-radius: 12px;
`

const OptionalBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  padding: 36px 24px;

  background-color: ${theme.colors.white};
  border: 1px solid ${theme.colors.grey_3};
  border-radius: 12px;
`

const InputBox = styled.div`
  position: relative;

  display: flex;
  gap: 20px;

  box-sizing: border-box;
  width: 100%;
  padding: 20px 0;

  &.center {
    align-items: center;

    @media ${theme.device.medium} {
      align-items: start;
    }
  }

  .title {
    min-width: 270px;

    font-family: NanumSquareNeo-Regular, sans-serif;
    font-size: 15px;
    font-weight: 400;
    line-height: 140%;
    color: ${theme.colors.grey_4};
    text-align: left;

    @media ${theme.device.medium} {
      width: 100%;
    }
  }

  .desc {
    font-family: NanumSquareNeo-Regular, sans-serif;
    font-size: 15px;
    font-weight: 400;
    line-height: 140%;
    color: ${theme.colors.grey_4};
  }

  .text-input {
    box-sizing: border-box;
    min-width: 360px;
    height: 49px;
    padding: 20px;

    font-family: NanumSquareNeo-Regular, sans-serif;

    background-color: ${theme.colors.grey_1};
    border: none;
    border-radius: 12px;

    @media ${theme.device.medium} {
      width: 100%;
      min-width: 100%;
    }
  }

  textarea {
    box-sizing: border-box;
    width: 100%;
    height: auto;
    min-height: 100px;
    padding: 20px;

    font-family: NanumSquareNeo-Regular, sans-serif;
    word-wrap: break-word;
    white-space: pre-wrap;

    background-color: ${theme.colors.grey_1};
    border: none;
    border-radius: 12px;

    @media ${theme.device.medium} {
      width: 100%;
    }
  }

  .thumbnail {
    width: 132px;
    height: 132px;
    object-fit: cover;
    border-radius: 10px;
  }

  @media ${theme.device.medium} {
    flex-direction: column;
    gap: 16px;
    align-items: start;
  }
`

const WarningText = styled.div`
  margin: 10px 5px;

  font-family: NanumSquareNeo-Regular, sans-serif;
  font-size: 12px;
  font-weight: 400;
  color: ${theme.colors.warning};
`

const BubbleBox = styled.div`
  position: relative;
  position: absolute;
  z-index: ${theme.zIndex.aboveDefault};
  top: 200px;
  left: 800px;

  display: flex;
  flex-direction: column;
  justify-content: space-between;

  box-sizing: border-box;
  width: 270px;
  height: 134px;
  padding: 20px;

  font-family: NanumSquareNeo-Regular, sans-serif;
  font-size: 18px;

  background-color: ${theme.colors.grey_1};
  border-radius: 12px;

  @media ${theme.device.medium} {
    top: 100px;
    left: 0;
  }
`

const ButtonBox = styled.div`
  display: flex;
  gap: 4px;

  /* width: 100%; */

  .button {
    cursor: pointer;

    display: flex;
    align-items: center;
    justify-content: center;

    width: 50%;
    height: 49px;

    border: 1px solid ${theme.colors.grey_3};
    border-radius: 12px;

    &.active {
      color: ${theme.colors.white};
      background-color: ${theme.colors.mint_3};
      border: none;
    }
  }
`

const Poligon = styled.div`
  position: absolute;
  z-index: 999;
  top: 20px;
  left: -25px;

  width: 26px;
  height: 26px;

  background-color: ${theme.colors.grey_1};
  clip-path: polygon(50% 50%, 100% 100%, 100% 0);

  @media ${theme.device.medium} {
    top: 80px;
  }
`

const SelectButtonBox = styled.div`
  display: flex;
  flex-flow: column wrap;
  gap: 20px;

  &.column {
    flex-direction: column;
  }

  .divider {
    width: 100%;
    min-width: 700px;
    margin: 20px 0;
    border-bottom: 1px solid ${theme.colors.grey_3};

    @media ${theme.device.large} {
      min-width: 0;
    }
  }
`

const SelectButton = styled.span`
  cursor: pointer;

  display: flex;
  gap: 4px;
  align-items: center;
  justify-content: center;

  box-sizing: border-box;
  width: fit-content;
  height: auto;
  padding: 12px 20px;

  font-family: NanumSquareNeo-Regular, sans-serif;
  font-size: 18px;
  line-height: 140%;

  border: 1px solid ${theme.colors.grey_3};
  border-radius: 12px;

  &.active {
    color: ${theme.colors.white};
    background-color: ${theme.colors.mint_3};
    border: none;
  }

  @media ${theme.device.medium} {
    width: 100%;
    font-size: 15px;
  }
`

const Notice = styled.div`
  font-family: NanumSquareNeo-Bold, sans-serif;
  font-size: 15px;
  line-height: 21px;
  color: ${theme.colors.warning};
`

const ApplyButton = styled.input`
  cursor: pointer;

  width: 200px;
  height: 58px;
  margin: 0 auto;

  font-family: NanumSquareNeo-Regular, sans-serif;
  font-size: 24px;
  font-weight: 400;
  color: ${theme.colors.white};

  background-color: ${theme.colors.grey_5};
  border: none;
  border-radius: 54px;

  @media ${theme.device.medium} {
    width: 100%;
    font-size: 18px;
  }
`

const SiiImage = styled.img`
  width: 580px;
  margin: 0 auto;

  @media ${theme.device.medium} {
    width: 100%;
  }
`

const Desc = styled.div`
  margin: 0 auto;

  font-family: NanumSquareNeo-Bold, sans-serif;
  font-size: 42px;
  font-weight: 500;
  line-height: 58.8px;

  @media ${theme.device.medium} {
    font-size: 28px;
    text-align: center;
  }
`

const CheckBoxWrapper = styled.div`
  display: flex;
  align-items: center;
`

const CheckBoxText = styled.span`
  font-size: 18px;
`

const StyledCheckbox = styled(Checkbox)`
  &.css-12wnr2w-MuiButtonBase-root-MuiCheckbox-root.Mui-checked,
  .css-12wnr2w-MuiButtonBase-root-MuiCheckbox-root.MuiCheckbox-indeterminate {
    color: ${theme.colors.mint_3};
  }
`

const NoticeAgreementBox = styled.div`
  cursor: pointer;

  display: flex;
  flex-direction: column;

  /* gap: 24px; */
  align-items: center;

  font-family: NanumSquareNeo-Regular, sans-serif;
  font-size: 12px;

  a {
    color: ${theme.colors.grey_4};
    text-decoration: underline;
  }
`

const FileLabel = styled.label`
  position: relative;

  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: center;
  justify-content: center;

  width: 132px;
  height: 132px;

  font-weight: 500;

  background: ${theme.colors.grey_1};
  border-radius: 10px;
`

const FileInput = styled.input`
  cursor: pointer;

  position: absolute;
  inset: 0;

  width: 100%;
  height: 100%;

  opacity: 0;
`

const TextArea = styled.textarea`
  overflow-y: scroll;
  white-space: pre-wrap;
`
